import React from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, Platform } from 'react-native';
import PlatformIcon from '../common/PlatformIcon';
import { MdLocationOn, MdPhone, MdSchedule, MdRestaurant, MdShoppingBag, MdDirectionsTransit } from 'react-icons/md';
import OpeningHoursRow from './OpeningHoursRow';
import { ORANGE_ACTION } from '../../constants/colors';

export interface MealCardProps {
  title: string;
  note?: string;
  restaurantName?: string;
  metaPairs?: Array<{ label: string; value: string }>;
  venue?: string;
  cuisine?: string;
  course?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  price?: number;
  currency?: string;
  startTime?: string;
  endTime?: string;
  imageUrl?: string;
  address?: string;
  phone?: string;
  openingHours?: Record<string, string> | string;
  actionIcon?: React.ReactNode;
  transportNote?: string;
  dayDate?: string; // 行程日期，用于智能显示对应星期的营业时间
  onImageClick?: () => void; // 图片点击回调
  headerContent?: React.ReactNode; // Custom content to replace the image (e.g. map)
}

const DEFAULT_PLACEHOLDER = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&h=400&fit=crop';

const formatOpeningHours = (hours: Record<string, string> | string | undefined, targetDate?: string): string | undefined => {
  if (!hours) return undefined;
  if (typeof hours === 'string') return hours;
  
  // If it's an object, try to show the appropriate day
  if (typeof hours === 'object') {
    // Map day names in various formats
    const dayMap: Record<string, string[]> = {
      'mon': ['星期一', '週一', '周一', 'monday', 'mon'],
      'tue': ['星期二', '週二', '周二', 'tuesday', 'tue'],
      'wed': ['星期三', '週三', '周三', 'wednesday', 'wed'],
      'thu': ['星期四', '週四', '周四', 'thursday', 'thu'],
      'fri': ['星期五', '週五', '周五', 'friday', 'fri'],
      'sat': ['星期六', '週六', '周六', 'saturday', 'sat'],
      'sun': ['星期天', '星期日', '週日', '周日', 'sunday', 'sun'],
    };
    
    // Determine target day (0=Sun, 1=Mon, ..., 6=Sat)
    const targetDay = (() => {
      if (targetDate) {
        try {
          return new Date(targetDate).getDay();
        } catch {
          return new Date().getDay();
        }
      }
      return new Date().getDay();
    })();
    
    // Convert to our key format
    const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const targetDayKey = dayKeys[targetDay];
    const targetAliases = dayMap[targetDayKey] || [];
    
    // Try to find matching entry
    for (const [key, value] of Object.entries(hours)) {
      const normalizedKey = key.toLowerCase().trim();
      if (targetAliases.some(alias => normalizedKey.includes(alias.toLowerCase())) ||
          normalizedKey === targetDayKey) {
        if (value && typeof value === 'string' && value.trim() && value !== '待查詢') {
          return value.trim();
        }
      }
    }
    
    // Fallback: show any valid value
    const values = Object.values(hours);
    const filledValues = values.filter(v => v && typeof v === 'string' && v.trim() && v !== '待查詢');
    if (filledValues.length > 0) {
      return filledValues[0];
    }
  }
  return undefined;
};

const MealCard: React.FC<MealCardProps> = ({ title, note, restaurantName, metaPairs, venue, cuisine, course, price, currency, startTime, endTime, imageUrl, address, phone, openingHours, actionIcon, transportNote, dayDate, onImageClick, headerContent }) => {
  // The previous code mapped course ('breakfast'|'lunch'|'dinner') to Chinese labels (早餐/午餐/晚餐).
  // That mapping is not relevant for current business logic, so we intentionally omit displaying course labels.
  const courseLabel = undefined;
  const formattedOpeningHours = formatOpeningHours(openingHours, dayDate);
  // Filter metaPairs: only show items with a non-empty value and avoid placeholder '待查詢'
  const visibleMetaPairs = Array.isArray(metaPairs) ? metaPairs.filter(p => p && p.value && String(p.value).trim() && String(p.value).trim() !== '待查詢') : [];
  // Local expand state for long note/description
  const [noteExpanded, setNoteExpanded] = React.useState(false);
  const shouldShowToggle = (() => {
    if (!note) return false;
    try {
      const len = String(note).length;
      if (len >= 80) return true;
      if (String(note).includes('\n')) return true;
      // crude heuristic: many spaces implies multiple words -> likely long
      if ((String(note).match(/\s+/g) || []).length > 12) return true;
      return false;
    } catch {
      return false;
    }
  })();
  
  return (
    <View style={styles.cardContainer}>
      {headerContent ? (
        <View style={styles.imageWrapper}>
          {headerContent}
        </View>
      ) : imageUrl ? (
        <View style={styles.imageWrapper}>
          {Platform.OS === 'web' && onImageClick ? (
            <div 
              onClick={onImageClick}
              style={{ cursor: 'pointer', width: '100%', height: '100%' }}
            >
              <Image source={{ uri: imageUrl }} style={styles.image} resizeMode="cover" />
            </div>
          ) : (
            <Image source={{ uri: imageUrl }} style={styles.image} resizeMode="cover" />
          )}
        </View>
      ) : null}
      <View style={styles.cardContent}>
        <View style={styles.rowTop}>
          <Text style={styles.title}>{title}</Text>
          <View style={styles.actions}>
              {actionIcon ? actionIcon : React.createElement(MdRestaurant as any, { size: 16, color: ORANGE_ACTION })}
            </View>
        </View>
        <View style={styles.metaRow}>
          {restaurantName ? <Text style={styles.metaText}>{restaurantName}</Text> : null}
          {venue ? <Text style={styles.metaText}>{venue}</Text> : null}
          {cuisine ? <Text style={styles.metaText}>{cuisine}</Text> : null}
          {typeof price === 'number' ? <Text style={styles.metaText}>{`${price}${currency ? ' ' + currency : ''}`}</Text> : null}
        </View>
        
        {/* Address row */}
        {address ? (
          <View style={styles.infoRow}>
            <View style={styles.iconWrapper}>
              {React.createElement(MdLocationOn as any, { size: 16, color: '#9CA3AF' })}
            </View>
            <Text style={styles.infoText} numberOfLines={2}>{address}</Text>
          </View>
        ) : null}
        
        {/* Phone row */}
        {phone ? (
          <View style={styles.infoRow}>
            <View style={styles.iconWrapper}>
              {React.createElement(MdPhone as any, { size: 16, color: '#9CA3AF' })}
            </View>
            <Text style={styles.infoText}>{phone}</Text>
          </View>
        ) : null}
        
        {/* Opening hours row (use OpeningHoursRow for consistent expand/today behavior)
            Keep the schedule icon on the left to match previous visual design. */}
        {openingHours ? (
          <View style={styles.infoRow}>
            <View style={styles.iconWrapper}>
              {React.createElement(MdSchedule as any, { size: 16, color: '#9CA3AF' })}
            </View>
            <View style={{ flex: 1 }}>
              <OpeningHoursRow label={''} value={openingHours as any} targetDate={dayDate} />
            </View>
          </View>
        ) : null}
        
        {transportNote ? (
          <View style={styles.infoRow}>
            <View style={styles.iconWrapper}>
              {React.createElement(MdDirectionsTransit as any, { size: 16, color: '#9CA3AF' })}
            </View>
            <Text style={styles.infoText} numberOfLines={3}>{transportNote}</Text>
          </View>
        ) : null}
        {note ? (
          <>
            <Text style={styles.note} numberOfLines={noteExpanded ? undefined : 2}>{note}</Text>
            {shouldShowToggle ? (
              <TouchableOpacity style={styles.noteToggleButton} onPress={() => setNoteExpanded(v => !v)}>
                <Text style={styles.noteToggleText}>{noteExpanded ? '收起 ▴' : '展开 ▾'}</Text>
              </TouchableOpacity>
            ) : null}
          </>
        ) : null}
        {/* Render metaPairs (e.g. 推薦菜) as labeled rows */}
        {visibleMetaPairs.length ? (
          <View style={{ marginTop: 6 }}>
            {visibleMetaPairs.map((p, idx) => {
              // 确保value是字符串，如果是对象则转换或跳过
              const displayValue = typeof p.value === 'object' && p.value !== null 
                ? JSON.stringify(p.value) 
                : String(p.value);
              return (
                <View key={idx} style={styles.metaPairRow}>
                  <Text style={styles.metaPairLabel}>{p.label}</Text>
                  <Text style={styles.metaPairValue}>{displayValue}</Text>
                </View>
              );
            })}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
    marginBottom: 12,
    // react-native-web deprecates shadow* props — use boxShadow on web and keep native props elsewhere
    ...Platform.select({
      web: {
        boxShadow: '0px 4px 12px rgba(15,23,42,0.06)',
      },
      default: {
        shadowColor: '#0F172A',
        shadowOpacity: 0.06,
        shadowOffset: { width: 0, height: 4 },
        shadowRadius: 12,
        elevation: 2,
      },
    }),
  },
  imageWrapper: { width: '100%', aspectRatio: 16 / 9, backgroundColor: '#F3F4F6', overflow: 'hidden' },
  image: { width: '100%', height: '100%' },
  cardContent: { paddingVertical: 14, paddingHorizontal: 12 },
  rowTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  title: { fontSize: 16, fontWeight: '600', color: '#111827', flex: 1 },
  // ensure the action icon is vertically centered relative to the title line
  actions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginLeft: 8, height: 20 },
  metaRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, gap: 6 },
  metaText: { fontSize: 13, color: '#6B7280', lineHeight: 18 },
  metaPairRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  metaPairLabel: { fontSize: 13, color: '#6B7280' },
  metaPairValue: { fontSize: 13, color: '#111827', fontWeight: '600' },
  infoRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8, gap: 8 },
  iconWrapper: { width: 16, height: 16, justifyContent: 'center', alignItems: 'center', flexShrink: 0, marginTop: 2 },
  infoText: { fontSize: 13, color: '#6B7280', lineHeight: 18, flex: 1 },
  note: { marginTop: 8, fontSize: 13, color: '#6B7280', lineHeight: 18 },
  noteToggleButton: { marginTop: 8, paddingVertical: 10, borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  noteToggleText: { fontSize: 14, color: ORANGE_ACTION },
});

export default MealCard;

